import { useCallback, useEffect } from 'react';

/** Never let the hero image be pushed entirely off the bottom of the screen. */
const MIN_SLIVER = 48;

/**
 * Fits the hero wordmark to its content column, and trims the deliberate
 * pause above it when a short viewport can't afford the designed value.
 *
 * Why measure at all: the CSS size is clamp(4rem, 20vw, 19rem) — driven by
 * the viewport, while the column is min(1280px, vw) minus a padding that
 * steps up at 1400px. The two can't track each other, so the word overflows
 * the column on wide screens and falls well short of it on narrow ones.
 * Measuring the rendered ink and solving for the size that spans the column
 * exactly makes the wordmark line up with the tagline and meta rule at
 * every width. The clamp stays in the stylesheet as the no-JS result.
 */
export function useHeroLayout({ heroRef, wordmarkRef, imageRef, enabled = true }) {
  const fitWordmark = useCallback(() => {
    const target = wordmarkRef.current;
    if (!target) return;
    const letters = target.querySelectorAll('[data-letter]');
    if (!letters.length) return;

    const column = target.parentElement;
    if (!column) return;
    const cs = window.getComputedStyle(column);
    const available =
      column.getBoundingClientRect().width -
      parseFloat(cs.paddingLeft) -
      parseFloat(cs.paddingRight);
    if (!(available > 0)) return;

    target.style.fontSize = '';
    // Ink scales linearly with font-size (letter-spacing is in em), so one
    // pass lands it; the second only cleans up sub-pixel rounding.
    for (let pass = 0; pass < 2; pass += 1) {
      const size = parseFloat(window.getComputedStyle(target).fontSize);
      const ink =
        letters[letters.length - 1].getBoundingClientRect().right -
        letters[0].getBoundingClientRect().left;
      if (!(ink > 0) || !(size > 0)) return;
      target.style.fontSize = `${size * (available / ink)}px`;
    }
  }, [wordmarkRef]);

  /**
   * The wordmark's height follows the column *width*, while the space above
   * it is budgeted in viewport *height*. On a wide but short screen the two
   * don't fit, and the hero image gets pushed off the bottom entirely. The
   * deliberate pause above the wordmark is the flexible part: it keeps its
   * designed value whenever there is room, and only gives back what it must
   * so a sliver of the image is always showing.
   */
  const fitHeroPause = useCallback(() => {
    const hero = heroRef.current;
    const image = imageRef.current;
    if (!hero || !image) return;

    hero.style.paddingTop = '';
    const designed = parseFloat(window.getComputedStyle(hero).paddingTop);
    if (!(designed > 0)) return;

    // Document position, so this is correct whatever the current scroll is.
    const imageDocTop = image.getBoundingClientRect().top + window.scrollY;
    const excess = imageDocTop - (window.innerHeight - MIN_SLIVER);
    if (excess > 0) {
      hero.style.paddingTop = `${Math.max(32, designed - excess)}px`;
    }
  }, [heroRef, imageRef]);

  const layoutHero = useCallback(() => {
    fitWordmark();
    fitHeroPause();
  }, [fitWordmark, fitHeroPause]);

  useEffect(() => {
    if (!enabled) return undefined;

    layoutHero();
    window.addEventListener('resize', layoutHero);

    // Archivo arrives after first paint; the fallback face has different
    // metrics, so the first fit is provisional until the real font is in.
    let cancelled = false;
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (cancelled) return;
        layoutHero();
        // The hero image's scroll effect caches this element's position,
        // and refitting moves it.
        window.dispatchEvent(new Event('resize'));
      });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('resize', layoutHero);
    };
  }, [enabled, layoutHero]);

  return layoutHero;
}
